import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface TimeSeriesChartProps {
  style?: { width: string; height: string };
  // [timestamp, value, unit]
  data: [string, number | string, string][];
  title?: string;

  // Enable auto unit scaling (useful for traffic charts)
  autoUnit?: boolean;

  // IANA timezone, e.g. "Asia/Shanghai", "Europe/Berlin"
  timezone?: string;
}

const LABEL_MAX_POINTS = 30;     // show value labels only when visible points <= this
const X_MIN_LABEL_PX = 90;       // each x label roughly needs this many pixels

// Traffic unit table (decimal, 1000-based)
const TRAFFIC_UNITS = [
  { unit: 'bps', factor: 1 },
  { unit: 'Kbps', factor: 1e3 },
  { unit: 'Mbps', factor: 1e6 },
  { unit: 'Gbps', factor: 1e9 },
  { unit: 'Tbps', factor: 1e12 },
] as const;

const normalizeTrafficUnit = (u: string): typeof TRAFFIC_UNITS[number]['unit'] | null => {
  const s = (u || '').trim().toLowerCase();

  if (!s) return null;
  if (s === 'bps' || s === 'bit/s' || s === 'bits/s' || s === 'b/s') return 'bps';
  if (s === 'kbps' || s === 'kbit/s' || s === 'kbits/s') return 'Kbps';
  if (s === 'mbps' || s === 'mbit/s' || s === 'mbits/s') return 'Mbps';
  if (s === 'gbps' || s === 'gbit/s' || s === 'gbits/s') return 'Gbps';
  if (s === 'tbps' || s === 'tbit/s' || s === 'tbits/s') return 'Tbps';

  if (u === 'bps' || u === 'Kbps' || u === 'Mbps' || u === 'Gbps' || u === 'Tbps') return u as any;

  return null;
};

const trafficToBps = (value: number, unit: string): number => {
  const canonical = normalizeTrafficUnit(unit);
  if (!canonical) return value;
  const item = TRAFFIC_UNITS.find(x => x.unit === canonical);
  return item ? value * item.factor : value;
};

const pickTrafficDisplayUnit = (maxBps: number) => {
  if (!Number.isFinite(maxBps) || maxBps <= 0) return TRAFFIC_UNITS[0];
  for (let i = TRAFFIC_UNITS.length - 1; i >= 0; i--) {
    if (maxBps >= TRAFFIC_UNITS[i].factor) return TRAFFIC_UNITS[i];
  }
  return TRAFFIC_UNITS[0];
};

function ensureTz(tz?: string) {
  const s = String(tz || '').trim();
  return s ? s : 'UTC';
}

// Accept ISO string, unix seconds, unix ms, numeric string
function parseTsToUnixMs(ts: any): number {
  if (ts === null || ts === undefined) return NaN;

  if (typeof ts === 'number') {
    return ts < 1e12 ? ts * 1000 : ts;
  }

  const s = String(ts).trim();
  if (!s) return NaN;

  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return n < 1e12 ? n * 1000 : n;
  }

  const d = dayjs.utc(s);
  return d.isValid() ? d.valueOf() : NaN;
}

function formatTsInTz(rawTs: any, tz: string, fmt: string) {
  const ms = parseTsToUnixMs(rawTs);
  if (!Number.isFinite(ms)) return String(rawTs ?? '');
  return dayjs(ms).tz(tz).format(fmt);
}

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
  style = { width: '100%', height: '100%' },
  data,
  title,
  autoUnit = false,
  timezone,
}) => {
  const tz = ensureTz(timezone);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [option, setOption] = useState<any>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const ro = new ResizeObserver(entries => {
      const w = Math.floor(entries[0]?.contentRect?.width || 0);
      setContainerWidth(w);
    });

    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const fmt2 = (n: any) => {
    const v = typeof n === 'number' ? n : Number(n);
    if (!Number.isFinite(v)) return '0.00';
    return v.toFixed(2);
  };

  const fmtSmart = (n: any) => {
    const v = typeof n === 'number' ? n : Number(n);
    if (!Number.isFinite(v)) return '0';
    return v.toFixed(2).replace(/\.?0+$/, '');
  };

  // Short x label format in unified TZ
  const fmtTsShort = (value: any) => {
    // MM-DD HH:mm
    return formatTsInTz(value, tz, 'MM-DD HH:mm');
  };

  // Longer tooltip timestamp format in unified TZ
  const fmtTsLong = (value: any) => {
    return formatTsInTz(value, tz, 'YYYY-MM-DD HH:mm:ss');
  };

  const computeMaxLabelsByWidth = (w: number) => {
    if (!w || w <= 0) return 10;
    return Math.max(2, Math.floor(w / X_MIN_LABEL_PX));
  };

  const getLabelIntervalByVisibleCount = (visibleCount: number, maxLabels: number) => {
    if (visibleCount <= maxLabels) return 0;
    return Math.max(1, Math.ceil(visibleCount / maxLabels) - 1);
  };

  useEffect(() => {
    if (!data || data.length === 0) {
      setOption(null);
      return;
    }

    const dateList = data.map(item => item[0]);

    const rawValues = data.map(item => {
      const v = typeof item[1] === 'number' ? item[1] : Number(item[1]);
      return Number.isFinite(v) ? v : 0;
    });

    let displayUnit = (data[0]?.[2] || '').trim();
    let valueList = rawValues;

    if (autoUnit) {
      const bpsValues = data.map((item, idx) => {
        const v = rawValues[idx];
        const u = item[2] || 'bps';
        return trafficToBps(v, u);
      });

      const maxBps = bpsValues.reduce((m, x) => (x > m ? x : m), 0);
      const chosen = pickTrafficDisplayUnit(maxBps);

      displayUnit = chosen.unit;
      valueList = bpsValues.map(v => v / chosen.factor);
    }

    const maxLabels = computeMaxLabelsByWidth(containerWidth);
    const interval = getLabelIntervalByVisibleCount(dateList.length, maxLabels);

    const rotate = containerWidth && containerWidth < 360 ? 45 : 0;
    const initialShowLabel = dateList.length <= LABEL_MAX_POINTS;

    setOption({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          if (!params || !params.length) return '';
          const p = params[0];

          // axisValue is the raw category value (timestamp string)
          const rawTs = p.axisValue ?? p.name;
          const ts = fmtTsLong(rawTs);

          return `${ts}: ${fmt2(p.data)} ${displayUnit}`;
        },
      },
      grid: {
        left: '10%',
        right: '5%',
        top: title ? '18%' : '5%',
        bottom: rotate ? '22%' : '15%',
      },
      xAxis: {
        type: 'category',
        data: dateList,
        axisLabel: {
          formatter: (value: any) => fmtTsShort(value),
          interval,
          rotate,
          hideOverlap: true,
          overflow: 'truncate',
          width: 80,
          ellipsis: '…',
          color: '#FFFFFF',
          fontSize: 10,
        },
        axisLine: { lineStyle: { color: '#FFFFFF' } },
        axisTick: {
          show: true,
          alignWithLabel: true,
          lineStyle: { color: '#FFFFFF', width: 1.5 },
          length: 6,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (val: number) => `${fmtSmart(val)} ${displayUnit}`,
          color: '#FFFFFF',
          fontSize: 12,
        },
        splitLine: { lineStyle: { type: 'dashed', color: '#666666' } },
        axisLine: { lineStyle: { color: '#FFFFFF' } },
      },
      dataZoom: [{ type: 'inside', start: 0, end: 100 }],
      series: [
        {
          type: 'line',
          data: valueList,
          showSymbol: false,
          sampling: 'lttb',
          lineStyle: { color: '#42b3f5' },
          areaStyle: { color: '#42b3f5', opacity: 0.5 },
          smooth: true,
          label: {
            show: initialShowLabel,
            color: '#FFFFFF',
            fontSize: 10,
            position: 'top',
            formatter: (p: any) => fmt2(p?.value),
          },
        },
      ],
      title: title
        ? {
            text: title,
            left: 'center',
            textStyle: { color: '#FFFFFF', fontSize: 14 },
          }
        : undefined,
    });
  }, [data, title, containerWidth, autoUnit, tz]);

  const onEvents = useMemo(() => {
    const computeVisibleCount = (params: any, total: number) => {
      const b = params?.batch?.[0] ?? params ?? {};
      const startValue = b.startValue;
      const endValue = b.endValue;

      if (Number.isFinite(startValue) && Number.isFinite(endValue)) {
        return Math.max(1, Math.abs(Number(endValue) - Number(startValue)) + 1);
      }

      const start = Number.isFinite(b.start) ? Number(b.start) : 0;
      const end = Number.isFinite(b.end) ? Number(b.end) : 100;

      const ratio = Math.max(0, Math.min(1, (end - start) / 100));
      return Math.max(1, Math.round(total * ratio));
    };

    const handleDataZoom = (params: any) => {
      setOption((prev: any) => {
        if (!prev?.xAxis?.data?.length || !prev?.series?.length) return prev;

        const total = prev.xAxis.data.length;
        const visibleCount = computeVisibleCount(params, total);

        const shouldShowLabel = visibleCount <= LABEL_MAX_POINTS;

        const maxLabels = containerWidth ? Math.max(2, Math.floor(containerWidth / X_MIN_LABEL_PX)) : 10;
        const nextInterval = visibleCount <= maxLabels ? 0 : Math.max(1, Math.ceil(visibleCount / maxLabels) - 1);
        const nextRotate = containerWidth && containerWidth < 360 ? 45 : 0;

        const prevInterval = prev?.xAxis?.axisLabel?.interval ?? 0;
        const prevRotate = prev?.xAxis?.axisLabel?.rotate ?? 0;
        const prevShowLabel = !!prev.series[0]?.label?.show;

        if (prevInterval === nextInterval && prevRotate === nextRotate && prevShowLabel === shouldShowLabel) {
          return prev;
        }

        return {
          ...prev,
          grid: {
            ...(prev.grid || {}),
            bottom: nextRotate ? '22%' : '15%',
          },
          xAxis: {
            ...(prev.xAxis || {}),
            axisLabel: {
              ...(prev.xAxis?.axisLabel || {}),
              interval: nextInterval,
              rotate: nextRotate,
              hideOverlap: true,
            },
          },
          series: prev.series.map((s: any, idx: number) => {
            if (idx !== 0) return s;
            return {
              ...s,
              label: {
                ...(s.label || {}),
                show: shouldShowLabel,
              },
            };
          }),
        };
      });
    };

    return { datazoom: handleDataZoom };
  }, [containerWidth]);

  if (!option) return <div>Loading...</div>;

  return (
    <div ref={containerRef} style={style}>
      <ReactECharts
        option={option}
        style={{ width: '100%', height: '100%' }}
        opts={{ renderer: 'canvas' }}
        onEvents={onEvents}
      />
    </div>
  );
};

export default TimeSeriesChart;
